<template>
  <div class="outer-container">
    <div
      class="player-container"
      ref="playerContainer"
      v-show="!isLoading"
    ></div>
    <div class="uk-position-center spinner-lab-container" v-if="isLoading">
      <spinner class="uk-position-center"></spinner>
    </div>
  </div>
</template>

<script>
import spinner from '@/components/ui-components/spinner/spinner'
import { LABPlayer } from '@/services/lab'
import environment from '@/plugins/environment'
import mixpanel from '@/services/mixpanel'
import { idAuthenticator } from '@/plugins/auth'
import { mapActions } from 'vuex'
import { LAB } from '../../../enums/clients'

const destroyPromises = []
let initializePromise = Promise.resolve()

export default {
  name: 'lab-player-renderer',
  components: {
    spinner
  },
  props: {
    activity: {
      type: Object,
      default: {}
    },
    region: {
      default: 'default',
      type: String
    }
  },
  data() {
    return {
      isLoading: true,
      labPlayer: null
    }
  },
  computed: {
    playerOptions() {
      let options = {
        root: environment.serviceUrlFor(LAB),
        environment: environment.getEnv(),
        region: this.region,
        language: this.$i18n.locale,

        collaborationPanelLayout: LABPlayer.PANEL_LAYOUT.SIMPLE,
        hideCollaboration: true,
        hideEdit: true,
        hideReset: true,
        hideReview: false,
        disableAudio: true,
        disableGamification: false,
        disableTracking: mixpanel.hasOptedOut(),
        skipStartScreen: true,
        skipActiveActivityWarning: true,

        onStart: this.onStart,
        onError: this.onError,
        contentServicesFn: this.loadContentServicesImage,
        collaborationState: {},
        webCollaborationState: {
          autoSave: false,
          autoRestore: false,
          enabled: false
        },
        id: {
          getAccessToken: () => {
            return idAuthenticator.getAccessToken()
          },
          getProfile: callback => {
            const userData = idAuthenticator.getUserData()
            // eslint-disable-next-line
            callback({
              name: userData.name,
              id: userData.id,
              email: userData.email
            })
          }
        },
        feature: s => this.$feature(s)
      }
      return options
    }
  },
  methods: {
    ...mapActions(['fetchContentProfile']),
    _createPlayer() {
      let activityCopy = JSON.parse(JSON.stringify(this.activity))
      this.labPlayer = new LABPlayer(
        this.$refs.playerContainer,
        activityCopy,
        this.playerOptions
      )
    },
    loadContentServicesImage(contentId, done) {
      this.fetchContentProfile([contentId])
        .then(result => {
          done(result[0].defaultPreviewUrl)
        })
        .catch(e => {
          done()
        })
    },
    mountLABPlayer() {
      const previousInitializePromise = initializePromise || Promise.resolve()
      initializePromise = new Promise(resolve => {
        previousInitializePromise
          .finally(() => {
            return this._suspendActivity()
          })
          .then(() => {
            return this._destroyPlayers()
          })
          .then(() => {
            this._createPlayer()
            return this.labPlayer.playerLoaded()
          })
          .then(() => {
            this.isLoading = false
            resolve()
          })
          .catch(error => {
            console.error(error)
            // Need to resolve or the player won't continue loading
            resolve()
          })
      })
      return initializePromise
    },
    onStart() {
      if (!this.labPlayer) {
        console.warn(`labPlayer not defined`)
        return
      }
      this.labPlayer.alignComponents(LABPlayer.ALIGN_LEFT)
      this.labPlayer.mute(false)
      this.labPlayer.setCollaborationVisible(false)
    },
    onError(error) {
      console.error(error)
    },
    _suspendActivity() {
      const setupPromise = this.startPromise || Promise.resolve()
      if (!this.labPlayer) {
        return Promise.resolve()
      }
      return setupPromise.then(() => {
        return Promise.resolve()
      })
    },

    _destroyPlayers() {
      if (this.labPlayer) {
        destroyPromises.push(
          this.labPlayer
            .destroy()
            .catch(() => {})
            .then(() => {
              this.labPlayer = null
            })
        )
      }
      return this._promiseAllCleanup(destroyPromises)
    },
    _promiseAllCleanup(promises) {
      const resolved = promises.slice()
      return new Promise(resolve => {
        // Promise.all will reject when it encounters the first failure,
        // so ensure that all promises are in a settled state (resolved/rejected)
        promises.forEach((promise, i) => {
          resolved[i] = promise
            .catch(() => {})
            .finally(() => {
              const index = promises.indexOf(promise)
              if (index !== -1) {
                promises.splice(index, 1)
              }
            })
        })
        // Now that all promiess have settled, we can safely do a Promise.all
        return Promise.all(resolved)
          .catch(() => {})
          .finally(resolve)
      })
    }
  },
  mounted() {
    this.mountLABPlayer()
  },
  beforeDestroy() {
    if (!this.labPlayer) {
      return
    }
    this._suspendActivity().finally(() => {
      this._destroyPlayers()
    })
  }
}
</script>

<style lang="scss" scoped>
.spinner-lab-container {
  height: 101%;
  width: 100%;
  background-color: black;
}

.outer-container {
  width: 100%;
  position: relative;
}

.player-container {
  width: 88.96% !important;
  height: 100% !important;
  margin: auto !important;
}
</style>
